/**
 * GOtaps — Notifications service
 *
 * Responsibilities:
 *  - Sign the current user into Firebase Auth via a custom token minted by backend
 *  - Register / unregister this device's FCM token with backend
 *  - Subscribe to the personal inbox + broadcast feed, merge into one stream
 *  - Mark-as-read, delete, listen for foreground FCM messages
 */
import {
  collection, query, where, orderBy, onSnapshot, doc,
  updateDoc, deleteDoc, getDocs, writeBatch, serverTimestamp, limit,
} from 'firebase/firestore';
import {
  db, auth, VAPID_KEY,
  getMessagingIfSupported, getToken, onMessage, signInWithCustomToken,
} from './firebase';

// ── Auth bridge ──────────────────────────────────────────────────
const RAW_API = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
const API = /\/api$/.test(RAW_API) ? RAW_API : `${RAW_API}/api`;
function authHeaders() {
  const token = localStorage.getItem('gotap_token');
  return { 'Content-Type':'application/json', 'Accept':'application/json',
           ...(token ? { Authorization:`Bearer ${token}` } : {}) };
}

export async function signInToFirebase() {
  if (auth.currentUser) return auth.currentUser;
  const res = await fetch(`${API}/notifications/firebase-token`, { headers: authHeaders() });
  if (!res.ok) throw new Error('Failed to mint Firebase token');
  const { firebase_token } = await res.json();
  const cred = await signInWithCustomToken(auth, firebase_token);
  return cred.user;
}

// ── Device token lifecycle ───────────────────────────────────────
export async function registerDeviceToken() {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return null;
  if (typeof Notification === 'undefined') return null;
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') return null;
  const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const fcmToken = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: reg });
  if (!fcmToken) return null;
  await fetch(`${API}/notifications/device-token`, {
    method:'POST', headers: authHeaders(),
    body: JSON.stringify({ fcm_token: fcmToken, platform:'web', user_agent: navigator.userAgent }),
  }).catch(()=>{});
  localStorage.setItem('gotap_fcm_token', fcmToken);
  return fcmToken;
}

export async function unregisterDeviceToken() {
  const fcmToken = localStorage.getItem('gotap_fcm_token');
  if (!fcmToken) return;
  await fetch(`${API}/notifications/device-token`, {
    method:'DELETE', headers: authHeaders(),
    body: JSON.stringify({ fcm_token: fcmToken }),
  }).catch(()=>{});
  localStorage.removeItem('gotap_fcm_token');
}

// ── Foreground push listener (shows in-app toast) ────────────────
export async function onForegroundMessage(cb) {
  const messaging = await getMessagingIfSupported();
  if (!messaging) return () => {};
  return onMessage(messaging, cb);
}

// ── Inbox subscription ──────────────────────────────────────────
// Returns an unsubscribe function. `cb(list)` receives merged, sorted rows.
export function subscribeToInbox(userId, audience, accountCreatedAt, cb) {
  let personal = [];
  let broadcasts = [];
  let reads = new Set();
  const emit = () => {
    const bcast = broadcasts
      .filter(b => !b.deletedAt)
      .filter(b => b.audience === 'all' || b.audience === audience)
      .filter(b => !accountCreatedAt || (b.createdAt?.toMillis?.() ?? 0) >= accountCreatedAt)
      .map(b => ({
        ...b,
        isBroadcast: true,
        readAt: reads.has(b.id) ? b.id : null, // use presence-of-id as read marker
      }));
    const all = [...personal, ...bcast].sort((a,b)=>{
      const ta = a.createdAt?.toMillis?.() ?? 0;
      const tb = b.createdAt?.toMillis?.() ?? 0;
      return tb - ta;
    });
    cb(all);
  };

  const u1 = onSnapshot(
    query(collection(db, 'inboxes', String(userId), 'items'),
          orderBy('createdAt','desc'), limit(100)),
    snap => { personal = snap.docs.map(d => ({ id:d.id, ...d.data(), isBroadcast:false })); emit(); }
  );
  const u2 = onSnapshot(
    query(collection(db, 'broadcasts'), orderBy('createdAt','desc'), limit(50)),
    snap => { broadcasts = snap.docs.map(d => ({ id:d.id, ...d.data() })); emit(); }
  );
  const u3 = onSnapshot(
    collection(db, 'userBroadcastReads', String(userId), 'reads'),
    snap => { reads = new Set(snap.docs.map(d => d.id)); emit(); }
  );
  return () => { u1(); u2(); u3(); };
}

// ── Mark-as-read / delete ────────────────────────────────────────
export async function markAsRead(userId, row) {
  if (row.isBroadcast) {
    await fetch(`${API}/notifications/broadcast-read/${row.id}`, {
      method:'POST', headers: authHeaders(),
    }).catch(()=>{});
    return;
  }
  await updateDoc(doc(db, 'inboxes', String(userId), 'items', row.id),
                  { readAt: serverTimestamp() });
}

export async function markAllRead(userId, rows) {
  const batch = writeBatch(db);
  let touched = 0;
  for (const r of rows) {
    if (r.readAt) continue;
    if (r.isBroadcast) continue;
    batch.update(doc(db,'inboxes', String(userId),'items',r.id), { readAt: serverTimestamp() });
    touched++;
  }
  if (touched) await batch.commit();
  // Broadcast reads handled server-side in bulk
  const bcastUnread = rows.filter(r => r.isBroadcast && !r.readAt).map(r => r.id);
  if (bcastUnread.length) {
    await fetch(`${API}/notifications/broadcast-read-bulk`, {
      method:'POST', headers: authHeaders(), body: JSON.stringify({ ids: bcastUnread }),
    }).catch(()=>{});
  }
}

export async function deleteNotification(userId, row) {
  if (row.isBroadcast) return; // broadcasts can't be deleted individually
  await deleteDoc(doc(db,'inboxes', String(userId),'items', row.id));
}
